import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, DocumentArrowUpIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../config';
import { BYPASS_AUTH } from '../config/development';
import { getAuthHeaders, SESSION_TOKEN_KEY } from '../api/workosAuth';
import { authFetch } from '../api/config';

interface ProjectUploadWizardProps {
  onSuccess?: (result: any) => void;
  onCancel?: () => void;
}

interface XerScheduleProjectRow {
  proj_id: string;
  proj_name: string;
  task_count: number;
}

interface UploadResult {
  import_id?: string;
  import_run_id: string;
  project_id?: string;
  project_name: string;
  status?: string;
  stats?: {
    file_size?: number;
    projects?: number;
    activities?: number;
  };
  /** Backend may return result from ingest (total_rows, row_counts, etc.) */
  result?: { total_rows?: number; row_counts?: Record<string, number> };
  message?: string;
  /** P6 projects in this import (multi-project XER). */
  xer_schedule_projects?: XerScheduleProjectRow[];
  xer_schedule_projects_need_choice?: boolean;
}

export const ProjectUploadWizard: React.FC<ProjectUploadWizardProps> = ({ 
  onSuccess, 
  onCancel 
}) => {
  const [step, setStep] = useState(1);
  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    organization: ''
  });
  const [xerFile, setXerFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  /** When XER has multiple P6 projects, user must pick one before continuing. */
  const [selectedXerProjId, setSelectedXerProjId] = useState<string | null>(null);
  const [xerScopeConfirmed, setXerScopeConfirmed] = useState(false);
  const [xerScopeSaving, setXerScopeSaving] = useState(false);
  const [xerScopeError, setXerScopeError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleProjectSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (projectData.name.trim()) {
      setStep(2);
      setError(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.xer')) {
        setError('Please select a .XER file');
        return;
      }
      setXerFile(file);
      setError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xer')) {
      setError('Please select a .XER file');
      return;
    }
    setXerFile(file);
    setError(null);
  };

  const handleFileUpload = async () => {
    if (!xerFile || !projectData.name) return;

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('project_name', projectData.name);
      formData.append('project_description', projectData.description);
      formData.append('project_organization', projectData.organization);
      formData.append('file', xerFile);

      let headers: HeadersInit = {};
      try {
        headers = await getAuthHeaders();
      } catch {
        setError('Authentication required. Please log in.');
        setUploading(false);
        return;
      }

      // Production / real login: Bearer is required (cookies often not sent cross-origin to API).
      // Local dev: backend accepts requests with no token (ENVIRONMENT=development); BYPASS_AUTH matches that.
      const hasAuth = headers && typeof headers === 'object' && 'Authorization' in headers;
      if (!hasAuth && !BYPASS_AUTH) {
        setError('Session expired or not authenticated. Please log in again.');
        setUploading(false);
        return;
      }

      // Do not set Content-Type: let the browser set multipart/form-data with boundary for FormData
      let response = await authFetch(`${API_BASE_URL}/upload/xer`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
        noRedirect: true,
      });

      // On 401: try token refresh once, then retry upload (handles expired token while tab was open)
      if (response.status === 401) {
        const refreshHeaders = await getAuthHeaders();
        const refreshRes = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: refreshHeaders,
        });
        if (refreshRes.ok) {
          const refreshData = await refreshRes.json().catch(() => ({}));
          if (refreshData?.access_token && typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(SESSION_TOKEN_KEY, refreshData.access_token);
          }
          response = await authFetch(`${API_BASE_URL}/upload/xer`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
            noRedirect: true,
          });
        }
      }

      if (response.status === 401) {
        window.location.href = '/login';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail ?? errorData.message;
        const msg = Array.isArray(detail) ? detail[0]?.msg ?? String(detail) : detail;
        throw new Error(msg || `Upload failed: ${response.status}`);
      }

      const apiResponse = await response.json();
      // Normalize API response to UploadResult (backend returns result with total_rows/row_counts, not stats)
      let scheduleProjects: XerScheduleProjectRow[] = Array.isArray(apiResponse.xer_schedule_projects)
        ? apiResponse.xer_schedule_projects
        : [];
      // Re-read canonical project list for this import to avoid stale/incomplete upload payloads.
      try {
        const importRunId = apiResponse.import_run_id;
        const projectUuid = apiResponse.project_id;
        if (importRunId && projectUuid) {
          const scheduleResp = await authFetch(
            `${API_BASE_URL}/v1/projects/${encodeURIComponent(projectUuid)}/xer-schedule-projects?version_id=${encodeURIComponent(importRunId)}`,
            {
              method: 'GET',
              credentials: 'include',
              noRedirect: true,
            }
          );
          if (scheduleResp.ok) {
            const scheduleBody = await scheduleResp.json();
            if (Array.isArray(scheduleBody?.projects)) {
              scheduleProjects = scheduleBody.projects;
            }
          }
        }
      } catch {
        // Keep upload payload fallback; do not block upload success.
      }
      const multiSchedule = scheduleProjects.length > 1;

      const result: UploadResult = {
        ...apiResponse,
        import_run_id: apiResponse.import_run_id ?? apiResponse.result?.import_run_id ?? '',
        project_id: apiResponse.project_id,
        project_name: apiResponse.project_name ?? '',
        status: apiResponse.message ?? 'Complete',
        stats: {
          file_size: xerFile.size ?? 0,
          projects: scheduleProjects.length || 1,
          activities: apiResponse.result?.row_counts?.TASK ?? 0,
        },
        xer_schedule_projects: scheduleProjects,
        xer_schedule_projects_need_choice: multiSchedule,
      };
      setUploadResult(result);
      setSelectedXerProjId(scheduleProjects[0]?.proj_id ?? null);
      setXerScopeConfirmed(!multiSchedule);
      setXerScopeError(null);
      setStep(3);
      // Do NOT call onSuccess here; call it when user clicks "View Projects" on step 3
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmXerScope = async () => {
    if (!uploadResult?.project_id || !selectedXerProjId) return;
    setXerScopeSaving(true);
    setXerScopeError(null);
    try {
      const headers = await getAuthHeaders();
      const scopeBase = `${API_BASE_URL}/v1/projects/${encodeURIComponent(uploadResult.project_id)}/xer-schedule-scope`;
      const scopeUrl =
        uploadResult.import_run_id?.trim().length
          ? `${scopeBase}?version_id=${encodeURIComponent(uploadResult.import_run_id)}`
          : scopeBase;
      const res = await authFetch(
        scopeUrl,
        {
          method: 'PATCH',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ xer_proj_id: selectedXerProjId }),
          credentials: 'include',
          noRedirect: true,
        }
      );
      if (res.status === 401) {
        window.location.href = '/login';
        return;
      }
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        const detail = errBody.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail[0]?.msg ?? res.statusText
              : detail?.message ?? res.statusText;
        throw new Error(msg || `Request failed: ${res.status}`);
      }
      setXerScopeConfirmed(true);
    } catch (err) {
      setXerScopeError(err instanceof Error ? err.message : 'Failed to save schedule scope');
    } finally {
      setXerScopeSaving(false);
    }
  };

  const handleFinish = () => {
    if (uploadResult) {
      onSuccess?.(uploadResult);
    }
    if (onCancel) {
      onCancel();
    } else {
      navigate('/app/projects');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const selectedScheduleProject = uploadResult?.xer_schedule_projects?.find(
    (row) => row.proj_id === selectedXerProjId
  );
  const displayedActivityCount =
    selectedScheduleProject?.task_count ??
    uploadResult?.stats?.activities ??
    uploadResult?.result?.row_counts?.TASK ??
    0;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-3xl font-bold text-nb-text-primary">Create New Project</h2>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-nb-text-muted hover:text-nb-text-primary transition-colors"
            >
              ✕
            </button>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center space-x-4">
          <div className={`flex items-center space-x-2 ${step >= 1 ? 'text-nb-accent' : 'text-nb-text-muted'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step > 1 ? 'bg-nb-success' : step === 1 ? 'bg-nb-accent' : 'bg-nb-surface-3'
            }`}>
              {step > 1 ? <CheckCircleIcon className="w-5 h-5 text-white" /> : '1'}
            </div>
            <span className="font-medium">Project Details</span>
          </div>

          <div className="flex-1 h-0.5 bg-nb-border-subtle" />

          <div className={`flex items-center space-x-2 ${step >= 2 ? 'text-nb-accent' : 'text-nb-text-muted'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step > 2 ? 'bg-nb-success' : step === 2 ? 'bg-nb-accent' : 'bg-nb-surface-3'
            }`}>
              {step > 2 ? <CheckCircleIcon className="w-5 h-5 text-white" /> : '2'}
            </div>
            <span className="font-medium">Upload XER File</span>
          </div>

          <div className="flex-1 h-0.5 bg-nb-border-subtle" />

          <div className={`flex items-center space-x-2 ${step >= 3 ? 'text-nb-success' : 'text-nb-text-muted'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step === 3 ? 'bg-nb-success' : 'bg-nb-surface-3'
            }`}>
              {step === 3 ? <CheckCircleIcon className="w-5 h-5 text-white" /> : '3'}
            </div>
            <span className="font-medium">Complete</span>
          </div>
        </div>
      </div>

      {/* Step 1: Project Details */}
      {step === 1 && (
        <div className="bg-nb-surface-2 backdrop-blur-sm rounded-2xl p-8 border border-nb-border-subtle">
          <form onSubmit={handleProjectSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-nb-text-secondary mb-3">
                Project Name *
              </label>
              <input
                type="text"
                value={projectData.name}
                onChange={(e) => setProjectData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 bg-nb-surface-3 border border-nb-border-subtle rounded-xl text-nb-text-primary focus:ring-2 focus:ring-nb-accent focus:border-nb-accent transition-all"
                placeholder="Enter project name..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-nb-text-secondary mb-3">
                Organization (Optional)
              </label>
              <input
                type="text"
                value={projectData.organization}
                onChange={(e) => setProjectData(prev => ({ ...prev, organization: e.target.value }))}
                className="w-full px-4 py-3 bg-nb-surface-3 border border-nb-border-subtle rounded-xl text-nb-text-primary focus:ring-2 focus:ring-nb-accent focus:border-nb-accent transition-all"
                placeholder="Enter organization name..."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-nb-text-secondary mb-3">
                Description (Optional)
              </label>
              <textarea
                value={projectData.description}
                onChange={(e) => setProjectData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-3 bg-nb-surface-3 border border-nb-border-subtle rounded-xl text-nb-text-primary focus:ring-2 focus:ring-nb-accent focus:border-nb-accent transition-all"
                placeholder="Enter project description..."
                rows={4}
              />
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="bg-nb-accent hover:bg-nb-accent-hover text-white font-semibold py-3 px-8 rounded-xl transition-colors"
              >
                Continue to File Upload
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Step 2: File Upload */}
      {step === 2 && (
        <div className="bg-nb-surface-2 backdrop-blur-sm rounded-2xl p-8 border border-nb-border-subtle">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-nb-text-secondary mb-3">
                XER File *
              </label>
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="border-2 border-dashed border-nb-border-subtle rounded-xl p-8 text-center hover:border-nb-border-strong transition-colors"
              >
                {xerFile ? (
                  <div className="space-y-4">
                    <div className="text-nb-success flex items-center justify-center space-x-2">
                      <CheckCircleIcon className="w-6 h-6" />
                      <span className="font-medium">{xerFile.name}</span>
                    </div>
                    <div className="text-sm text-nb-text-muted">
                      {formatFileSize(xerFile.size)}
                    </div>
                    <button
                      onClick={() => setXerFile(null)}
                      className="text-nb-accent hover:text-nb-accent-soft text-sm underline"
                    >
                      Choose different file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <DocumentArrowUpIcon className="mx-auto h-16 w-16 text-nb-text-muted" />
                    <div className="text-nb-text-muted">
                      <p className="text-lg mb-2">Drop your XER file here or click to browse</p>
                      <p className="text-sm">Supports Primavera P6 .XER files up to 100MB</p>
                    </div>
                    <input
                      type="file"
                      accept=".xer"
                      onChange={handleFileChange}
                      className="hidden"
                      id="xer-file-input"
                    />
                    <label
                      htmlFor="xer-file-input"
                      className="inline-block bg-nb-surface-3 hover:bg-nb-surface-2 text-nb-text-primary font-semibold py-3 px-6 rounded-lg cursor-pointer transition-colors"
                    >
                      Choose File
                    </label>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-nb-danger/20 border border-nb-danger rounded-xl p-4">
                <div className="text-nb-danger">{error}</div>
                {(error.includes('Session') || error.includes('log in') || error.includes('Authentication')) && (
                  <a
                    href="/login"
                    className="inline-block mt-2 text-nb-accent hover:text-nb-accent-hover font-medium"
                  >
                    Log in again →
                  </a>
                )}
              </div>
            )}

            <div className="flex space-x-4">
              <button
                onClick={() => setStep(1)}
                className="flex items-center space-x-2 bg-nb-surface-3 hover:bg-nb-surface-2 text-nb-text-primary font-semibold py-3 px-6 rounded-xl transition-colors"
                disabled={uploading}
              >
                <ChevronLeftIcon className="w-4 h-4" />
                <span>Back</span>
              </button>
              <button
                onClick={handleFileUpload}
                disabled={!xerFile || uploading}
                className="flex-1 bg-nb-accent hover:bg-nb-accent-hover disabled:bg-nb-surface-3 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-xl transition-colors"
              >
                {uploading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-nb-text-primary" />
                    <span>Processing...</span>
                  </div>
                ) : (
                  'Create Project'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Success */}
      {step === 3 && uploadResult && (
        <div className="bg-nb-surface-2 backdrop-blur-sm rounded-2xl p-8 border border-nb-border-subtle">
          <div className="text-center space-y-6">
            <div className="text-nb-success">
              <CheckCircleIcon className="mx-auto h-16 w-16 mb-4" />
              <h3 className="text-2xl font-bold text-nb-text-primary mb-2">Project Created Successfully!</h3>
              <p className="text-nb-text-secondary">Your XER file has been processed and your project is ready.</p>
            </div>

            <div className="bg-nb-surface-3 rounded-xl p-6 text-left">
              <h4 className="text-lg font-semibold text-nb-text-primary mb-4">Project Details</h4>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-nb-text-muted">Project Name:</span>
                  <span className="text-nb-text-primary font-medium">{uploadResult.project_name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-nb-text-muted">File Size:</span>
                  <span className="text-nb-text-primary">{formatFileSize(uploadResult.stats?.file_size ?? 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-nb-text-muted">P6 projects in file:</span>
                  <span className="text-nb-text-primary">{uploadResult.stats?.projects ?? 1}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-nb-text-muted">Activities:</span>
                  <span className="text-nb-text-primary">{displayedActivityCount.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {uploadResult.xer_schedule_projects_need_choice &&
              (uploadResult.xer_schedule_projects?.length ?? 0) > 1 && (
                <div className="bg-nb-surface-3 rounded-xl p-4 text-left space-y-4 border border-nb-border-subtle">
                  {!xerScopeConfirmed ? (
                    <>
                      <p className="text-sm text-nb-text-secondary">
                        This file contains multiple Primavera projects. Select which one this app project should use
                        for metrics and dashboards (project id, name, and activity count).
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="border-b border-nb-border-subtle text-nb-text-muted">
                              <th className="text-left py-2 pr-4 font-medium w-10" />
                              <th className="text-left py-2 pr-4 font-medium">Project ID</th>
                              <th className="text-left py-2 pr-4 font-medium">Name</th>
                              <th className="text-right py-2 font-medium">Activities</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.xer_schedule_projects!.map((row) => (
                              <tr key={row.proj_id} className="border-b border-nb-border-subtle/60">
                                <td className="py-2 pr-4 align-top">
                                  <input
                                    type="radio"
                                    name="xer-schedule-proj"
                                    className="accent-nb-accent"
                                    checked={selectedXerProjId === row.proj_id}
                                    onChange={() => setSelectedXerProjId(row.proj_id)}
                                    aria-label={`Select P6 project ${row.proj_id}`}
                                  />
                                </td>
                                <td className="py-2 pr-4 align-top font-mono text-xs">{row.proj_id}</td>
                                <td className="py-2 pr-4 align-top">{row.proj_name}</td>
                                <td className="py-2 text-right align-top tabular-nums">
                                  {row.task_count.toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {xerScopeError && (
                        <div className="text-sm text-nb-danger" role="alert">
                          {xerScopeError}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={handleConfirmXerScope}
                        disabled={!selectedXerProjId || xerScopeSaving}
                        className="w-full bg-nb-surface-2 hover:bg-nb-surface-3 border border-nb-border-subtle text-nb-text-primary font-semibold py-3 px-6 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {xerScopeSaving ? 'Saving…' : 'Confirm schedule project'}
                      </button>
                    </>
                  ) : (
                    <p className="text-sm text-nb-success">Schedule project selected. You can continue.</p>
                  )}
                </div>
              )}

            <button
              onClick={handleFinish}
              disabled={
                !!(
                  uploadResult.xer_schedule_projects_need_choice &&
                  (uploadResult.xer_schedule_projects?.length ?? 0) > 1 &&
                  !xerScopeConfirmed
                )
              }
              className="bg-nb-accent hover:bg-nb-accent-hover text-white font-semibold py-3 px-8 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              View Projects
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectUploadWizard;
